import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Tooltip
} from '@mui/material';
import {
    Add as IconAdd,
    CheckCircle as IconCheck,
    Cancel as IconReject,
    Block as IconAnnul,
    Assessment as IconReport,
    Timeline as IconKardex,
    ArrowUpward as IconEntrada,
    ArrowDownward as IconSalida,
    HourglassEmpty as IconPending
} from '@mui/icons-material';
import moment from 'moment';
import { Link } from 'react-router-dom';

import {
    getsKardex,
    getsArticulo,
    getsTiposMovimientoKardex,
    registrarMovimientoKardex,
    aprobarMovimientoKardex,
    rechazarMovimientoKardex,
    anularMovimientoKardex
} from '../../services/inventarioService';
import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import MovimientoKardexDialog from '../../components/Inventario/MovimientoKardexDialog';

const KardexIndex = () => {
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [movimientos, setMovimientos] = useState([]);
    const [articulos, setArticulos] = useState([]);
    const [tiposMovimiento, setTiposMovimiento] = useState([]);
    const [cargando, setCargando] = useState(false);

    // Filtros
    const [idArticulo, setIdArticulo] = useState(0);
    const [idTipoMovimiento, setIdTipoMovimiento] = useState(0);
    const [estado, setEstado] = useState('');
    const [fechaInicio, setFechaInicio] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [fechaFin, setFechaFin] = useState(moment().endOf('month').format('YYYY-MM-DD'));

    // Modal
    const [dialogOpen, setDialogOpen] = useState(false);

    const cargarMaestros = useCallback(async () => {
        try {
            const [artRes, tipRes] = await Promise.all([
                getsArticulo({ idFilial: idFilial || 0, soloActivos: true }),
                getsTiposMovimientoKardex()
            ]);
            setArticulos(artRes || []);
            setTiposMovimiento(tipRes || []);
        } catch (err) {
            console.error('Error cargando maestros:', err);
        }
    }, [idFilial]);

    const cargarMovimientos = useCallback(async () => {
        setCargando(true);
        try {
            const data = await getsKardex({
                idFilial: idFilial || 0,
                idArticulo: idArticulo || 0,
                idTipoMovimiento: idTipoMovimiento || 0,
                estado,
                fechaInicio: fechaInicio ? moment(fechaInicio).toISOString() : null,
                fechaFin: fechaFin ? moment(fechaFin).toISOString() : null
            });
            setMovimientos(data || []);
        } catch (err) {
            console.error('Error cargando kardex:', err);
        } finally {
            setCargando(false);
        }
    }, [idFilial, idArticulo, idTipoMovimiento, estado, fechaInicio, fechaFin]);

    useEffect(() => {
        cargarMaestros();
    }, [cargarMaestros]);

    useEffect(() => {
        cargarMovimientos();
    }, [cargarMovimientos]);

    const handleGuardarMovimiento = async (model, requiereAprobacion) => {
        try {
            await registrarMovimientoKardex(model, requiereAprobacion);
            setDialogOpen(false);
            cargarMovimientos();
            cargarMaestros();
        } catch (err) {
            alert('Error registrando movimiento: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleAprobar = async (idKardex) => {
        const obs = prompt('Observación opcional de aprobación:');
        if (obs !== null) {
            try {
                await aprobarMovimientoKardex(idKardex, obs);
                cargarMovimientos();
                cargarMaestros();
            } catch (err) {
                alert('Error al aprobar: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    const handleRechazar = async (idKardex) => {
        const motivo = prompt('Ingrese el motivo de rechazo:');
        if (motivo) {
            try {
                await rechazarMovimientoKardex(idKardex, motivo);
                cargarMovimientos();
            } catch (err) {
                alert('Error al rechazar: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    const handleAnular = async (idKardex) => {
        const motivo = prompt('Ingrese el motivo de anulación del movimiento:');
        if (motivo) {
            try {
                await anularMovimientoKardex(idKardex, motivo);
                cargarMovimientos();
                cargarMaestros();
            } catch (err) {
                alert('Error al anular: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    // Cálculos de resumen
    const totalEntradas = movimientos.filter((m) => m.estado === 'APROBADO').reduce((acc, m) => acc + (m.totalEntrada || 0), 0);
    const totalSalidas = movimientos.filter((m) => m.estado === 'APROBADO').reduce((acc, m) => acc + (m.totalSalida || 0), 0);
    const pendientes = movimientos.filter((m) => m.estado === 'PENDIENTE').length;

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabecera */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconKardex color="primary" /> Movimientos de Inventario y Kardex
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control de ingresos, salidas de materiales, aprobaciones y saldos ponderados por filial.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        component={Link}
                        to="/inventario/reportes"
                        variant="outlined"
                        color="secondary"
                        startIcon={<IconReport />}
                        sx={{ textTransform: 'none' }}
                    >
                        Reportes de Kardex
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<IconAdd />}
                        onClick={() => setDialogOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Registrar Movimiento
                    </Button>
                </Stack>
            </Stack>

            {/* KPIs Rápidos */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#f0fdf4', borderLeft: '4px solid #16a34a' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                TOTAL INGRESOS / COMPRAS (PERIODO)
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#15803d', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconEntrada fontSize="small" /> S/ {totalEntradas.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: '#fef2f2', borderLeft: '4px solid #dc2626' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                TOTAL SALIDAS / CONSUMOS (PERIODO)
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconSalida fontSize="small" /> S/ {totalSalidas.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ bgcolor: pendientes > 0 ? '#fffbeb' : '#f8fafc', borderLeft: `4px solid ${pendientes > 0 ? '#f59e0b' : '#94a3b8'}` }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                PENDIENTES DE APROBACIÓN
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: pendientes > 0 ? '#d97706' : '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconPending fontSize="small" /> {pendientes} {pendientes === 1 ? 'movimiento' : 'movimientos'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filtros */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2.5}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            setIdFilial={setIdFilial}
                            bloqueado={bloqueado}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Artículo</InputLabel>
                            <Select
                                value={idArticulo}
                                onChange={(e) => setIdArticulo(e.target.value)}
                                label="Artículo"
                            >
                                <MenuItem value={0}>-- Todos los Artículos --</MenuItem>
                                {articulos.map((a) => (
                                    <MenuItem key={a.id} value={a.id}>
                                        {a.codigo} - {a.nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Estado</InputLabel>
                            <Select
                                value={estado}
                                onChange={(e) => setEstado(e.target.value)}
                                label="Estado"
                            >
                                <MenuItem value="">-- Todos --</MenuItem>
                                <MenuItem value="APROBADO">Aprobado</MenuItem>
                                <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                                <MenuItem value="RECHAZADO">Rechazado</MenuItem>
                                <MenuItem value="ANULADO">Anulado</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabla Kardex */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Sede</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Artículo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tipo Movimiento</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Doc. Sustento</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Entrada</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Salida</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Saldo Cant.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Saldo Valor (S/)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Estado</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cargando ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : movimientos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No se encontraron registros de kardex con los filtros seleccionados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                movimientos.map((m) => {
                                    const chipColor =
                                        m.estado === 'APROBADO' ? 'success' :
                                        m.estado === 'PENDIENTE' ? 'warning' :
                                        m.estado === 'RECHAZADO' ? 'error' : 'default';

                                    return (
                                        <TableRow key={m.id} hover>
                                            <TableCell>{moment(m.fechaMovimiento).format('DD/MM/YYYY HH:mm')}</TableCell>
                                            <TableCell>{m.filial}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {m.nombreArticulo}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {m.codigoArticulo} {m.unidadMedida ? `(${m.unidadMedida})` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{m.tipoMovimiento}</Typography>
                                                {m.detalleGlosa && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {m.detalleGlosa}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{m.documentoSustento}</TableCell>
                                            <TableCell align="right" sx={{ color: '#16a34a', fontWeight: m.cantidadEntrada > 0 ? 'bold' : 'normal' }}>
                                                {m.cantidadEntrada > 0 ? `+${Number(m.cantidadEntrada).toFixed(2)}` : '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#dc2626', fontWeight: m.cantidadSalida > 0 ? 'bold' : 'normal' }}>
                                                {m.cantidadSalida > 0 ? `-${Number(m.cantidadSalida).toFixed(2)}` : '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {Number(m.cantidadSaldo).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">
                                                S/ {Number(m.totalSaldo).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={m.estado} color={chipColor} size="small" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    {m.estado === 'PENDIENTE' && (
                                                        <>
                                                            <Tooltip title="Aprobar Movimiento">
                                                                <IconButton size="small" color="success" onClick={() => handleAprobar(m.id)}>
                                                                    <IconCheck fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Rechazar Movimiento">
                                                                <IconButton size="small" color="error" onClick={() => handleRechazar(m.id)}>
                                                                    <IconReject fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {m.estado === 'APROBADO' && (
                                                        <Tooltip title="Anular Movimiento">
                                                            <IconButton size="small" color="default" onClick={() => handleAnular(m.id)}>
                                                                <IconAnnul fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modal de Registro de Movimiento */}
            <MovimientoKardexDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleGuardarMovimiento}
                filiales={filiales}
                idFilialDefault={idFilial}
                articulos={articulos}
                tiposMovimiento={tiposMovimiento}
            />
        </Box>
    );
};

export default KardexIndex;
